import "./statementDetail.scss";
import { BRAND } from "constants/brand";
import { Ring } from "@uiball/loaders";
import { NavLink, useParams } from "react-router-dom";
import { useRef } from "react";
import { useSelector } from "react-redux";
import { Toast } from "primereact/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faCheckCircle, faBan } from "@fortawesome/free-solid-svg-icons";

import {
    useGetStatement,
    useResendStatementWhatsapp,
    useSetStatementPayment,
} from "hooks/useStatements";
import { formatStatNumber } from "utils/percentage";
import { formatMoney } from "utils/money";
import { PaymentStatusBadge } from "../Statements";
import { ORDER_STATUSES } from "constants/orderStatus";
import { formatDate, formatDateTime } from "utils/dateFormat";

/**
 * Statement detail page (`/statements/:id`). Shows the persisted
 * statement metadata, the snapshotted WhatsApp body that was sent, the
 * delivery status with a resend control, and the orders included in the
 * statement.
 *
 * Read-only — there is no edit/delete intentionally; statements are
 * immutable once sent. Corrections are made by issuing a new statement.
 */
const StatementDetail = () => {
    const { id } = useParams();
    const toast = useRef(null);
    const adminInfo = useSelector((state) => state.auth.userData);
    const isVendor = adminInfo?.role === "vendor";
    const { statement, orders, loading, error } = useGetStatement(id);
    const resendMutation = useResendStatementWhatsapp();
    const paymentMutation = useSetStatementPayment();

    const showMessage = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    if (loading) {
        return (
            <div className="loadingSpinner">
                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
            </div>
        );
    }

    if (error || !statement) {
        return (
            <div className="items">
                <h4>تعذر تحميل كشف الحساب</h4>
                {error ? <p>{error}</p> : null}
            </div>
        );
    }

    const handleResend = async () => {
        try {
            const data = await resendMutation.mutateAsync(statement.id);
            const sendOk = data?.statement?.whatsapp_send_status === "sent";
            showMessage(
                sendOk ? "success" : "warn",
                sendOk ? "تم الإرسال" : "فشل الإرسال",
                sendOk
                    ? "تمت إعادة إرسال كشف الحساب على الواتساب"
                    : "فشلت إعادة الإرسال — تحقق من الإعدادات",
            );
        } catch (err) {
            showMessage(
                "error",
                "تعذر الإرسال",
                err?.response?.data?.message || err?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    const handleTogglePayment = async () => {
        const nextIsPaid = !statement.is_paid;
        try {
            await paymentMutation.mutateAsync({ id: statement.id, isPaid: nextIsPaid });
            showMessage(
                "success",
                nextIsPaid ? "تم تأكيد الدفع" : "تم إلغاء الدفع",
                nextIsPaid ? "تم تحديد هذا الكشف كمدفوع" : "تم إلغاء حالة الدفع لهذا الكشف",
            );
        } catch (err) {
            showMessage(
                "error",
                "تعذر تحديث حالة الدفع",
                err?.response?.data?.message || err?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    return (
        <div className="items" style={{ padding: "20px" }}>
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3>كشف حساب #{statement.id}</h3>
                {!isVendor ? (
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                            onClick={handleTogglePayment}
                            disabled={paymentMutation.isPending}
                            style={{
                                backgroundColor: statement.is_paid ? "#dc3545" : "#28a745",
                                color: "#fff",
                                opacity: paymentMutation.isPending ? 0.6 : 1,
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            <FontAwesomeIcon icon={statement.is_paid ? faBan : faCheckCircle} />
                            {paymentMutation.isPending
                                ? "جارٍ التحديث..."
                                : statement.is_paid
                                  ? "إلغاء الدفع"
                                  : "تأكيد الدفع"}
                        </button>
                        <button
                            onClick={handleResend}
                            disabled={resendMutation.isPending}
                            style={{
                                backgroundColor: BRAND,
                                color: "#fff",
                                opacity: resendMutation.isPending ? 0.6 : 1,
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            <FontAwesomeIcon icon={faWhatsapp} />
                            {resendMutation.isPending ? "جارٍ الإرسال..." : "إعادة إرسال الواتساب"}
                        </button>
                    </div>
                ) : null}
            </div>

            <div className="stats-container2" style={{ marginTop: "20px" }}>
                <div className="stat-box">
                    <h4>المطعم</h4>
                    <p>{statement.vendor?.user?.name || "—"}</p>
                </div>
                <div className="stat-box">
                    <h4>الفترة</h4>
                    <p dir="ltr" style={{ unicodeBidi: "isolate", whiteSpace: "nowrap" }}>
                        {formatPeriod(statement.start_date)} → {formatPeriod(statement.end_date)}
                    </p>
                </div>
                <div className="stat-box">
                    <h4>عدد الطلبات</h4>
                    <p>{formatStatNumber(statement.orders_count)}</p>
                </div>
                <div className="stat-box">
                    <h4>النسبة</h4>
                    <p>{formatStatNumber(statement.percentage)}%</p>
                </div>
            </div>

            <div className="stats-container2" style={{ marginTop: "10px" }}>
                <div className="stat-box">
                    <h4>إجمالي المبيعات</h4>
                    <p>{formatMoney(statement.subtotal)}</p>
                </div>
                <div className="stat-box">
                    <h4>إجمالي الشحن</h4>
                    <p>{formatMoney(statement.shipping_total)}</p>
                </div>
                <div className="stat-box">
                    <h4>عمولة التطبيق</h4>
                    <p>{formatMoney(statement.commission)}</p>
                </div>
                <div className="stat-box">
                    <h4>الصافي المستحق</h4>
                    <p>{formatMoney(statement.net_amount)}</p>
                </div>
            </div>

            <div style={{ marginTop: "20px" }}>
                <strong>حالة الدفع: </strong>
                <PaymentStatusBadge isPaid={statement.is_paid} />
                {statement.is_paid && statement.paid_at ? (
                    <span style={{ marginInlineStart: "12px" }}>
                        <strong>تم الدفع في:</strong>{" "}
                        <bdi dir="ltr">{formatDateTime(statement.paid_at)}</bdi>
                    </span>
                ) : null}
                {statement.is_paid && statement.paidBy?.name ? (
                    <span style={{ marginInlineStart: "12px" }}>
                        <strong>بواسطة:</strong> {statement.paidBy.name}
                    </span>
                ) : null}
            </div>

            <div style={{ marginTop: "20px" }}>
                <strong>حالة الواتساب: </strong>
                <WhatsappStatusBadge status={statement.whatsapp_send_status} />
                {statement.whatsapp_phone ? (
                    <span style={{ marginInlineStart: "12px" }}>
                        <strong>الرقم:</strong> {statement.whatsapp_phone}
                    </span>
                ) : null}
                {statement.sent_at ? (
                    <span style={{ marginInlineStart: "12px" }}>
                        <strong>أُرسلت في:</strong>{" "}
                        <bdi dir="ltr">{formatDateTime(statement.sent_at)}</bdi>
                    </span>
                ) : null}
                {statement.whatsapp_error ? (
                    <div style={{ color: "red", marginTop: "8px" }}>
                        <strong>سبب الفشل:</strong> {statement.whatsapp_error}
                    </div>
                ) : null}
            </div>

            <div style={{ marginTop: "20px" }}>
                <h5>نص رسالة الواتساب المرسلة</h5>
                <pre className="statement-message">{statement.whatsapp_message}</pre>
            </div>

            <div style={{ marginTop: "20px" }}>
                <h5>الطلبات المتضمنة ({orders.length})</h5>
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>الاسم</th>
                                <th>الإجمالي (₪)</th>
                                <th>الشحن (₪)</th>
                                <th>المبلغ (₪)</th>
                                <th>الحالة</th>
                                <th>تاريخ الإنشاء</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.length ? (
                                orders.map((o) => (
                                    <tr key={o.id}>
                                        <td>
                                            <NavLink
                                                to={`/orders/${o.id}`}
                                                style={{ color: "blue" }}
                                            >
                                                {o.id}
                                            </NavLink>
                                        </td>
                                        <td>{o.name}</td>
                                        <td>{formatMoney(o.subtotal)}</td>
                                        <td>{formatMoney(o.shipping)}</td>
                                        <td>{formatMoney(o.total)}</td>
                                        <td>
                                            <OrderStatusBadge status={o.status} />
                                        </td>
                                        <td>
                                            <bdi dir="ltr">{formatDateTime(o.createdAt)}</bdi>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7}>
                                        <h4 style={{ textAlign: "center", padding: "20px" }}>
                                            لا توجد طلبات
                                        </h4>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

/**
 * Renders an order's status enum value as an Arabic-labelled coloured
 * pill. Same color palette used by the orders-page status dropdown
 * (constants/orderStatus.js) so the badges stay consistent across the app.
 */
const ORDER_STATUS_AR = ORDER_STATUSES.reduce((entries, entry) => {
    entries[entry.en] = { ar: entry.ar, color: entry.color };
    return entries;
}, {});

const OrderStatusBadge = ({ status }) => {
    const entry = ORDER_STATUS_AR[status];
    return (
        <span
            style={{
                background: entry?.color ?? "#6c757d",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.85em",
                whiteSpace: "nowrap",
            }}
        >
            {entry?.ar ?? status}
        </span>
    );
};

const WhatsappStatusBadge = ({ status }) => {
    const colors = {
        sent: "#28a745",
        pending: "#ffc107",
        failed: "#dc3545",
    };
    const labels = {
        sent: "تم الإرسال",
        pending: "قيد الإرسال",
        failed: "فشل الإرسال",
    };
    return (
        <span
            style={{
                background: colors[status] ?? "#6c757d",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.85em",
            }}
        >
            {labels[status] ?? status}
        </span>
    );
};

const formatPeriod = (value) => formatDate(value) ?? "";

export default StatementDetail;
